import Link from "next/link";
import { SITE_URL } from "./site";
import { marketingContainerClassName } from "./marketing-layout";
import { useCaseLinksPrimary, useCaseLinksSecondary } from "./site-links";

const SITE_HOST = new URL(SITE_URL).hostname;
const CONTACT_EMAIL = `contact@${SITE_HOST}`;

const footerLinkClassName =
  "text-[0.8rem] text-zinc-600 outline-brand hover:text-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

const footerMetaLinkClassName =
  "text-[0.8rem] text-zinc-500 outline-brand hover:text-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

const footerHeadingClassName =
  "text-[0.8rem] font-semibold uppercase tracking-wide text-zinc-800";

const productLinks = [
  { href: "/markdown-editor", label: "Editor" },
  { href: "/convert-md-to-pdf", label: "Upload" },
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#faq", label: "FAQ" },
] as const;

const companyLinks = [
  { href: "/contact-us", label: "Contact Us" },
  { href: "/privacy", label: "Privacy Policy" },
] as const;

export function SiteFooter() {
  return (
    <footer className="bg-zinc-100 py-10 text-[0.8rem]">
      <div
        className={`${marketingContainerClassName} flex flex-col gap-10`}
      >
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-3 sm:col-span-2 lg:col-span-1">
            <Link
              href="/"
              className="flex w-fit items-center gap-2 outline-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/logo.svg"
                alt=""
                width={20}
                height={20}
                className="size-5 shrink-0"
              />
              <span className="font-medium text-zinc-800">
                Convert MD to PDF
              </span>
            </Link>
            <p className="max-w-[28ch] leading-normal text-zinc-600">
              Convert Markdown to PDF with a live preview and selectable text.
              No sign-up, no watermark.
            </p>
          </div>

          <nav aria-labelledby="footer-product-heading" className="flex flex-col gap-3">
            <h2 id="footer-product-heading" className={footerHeadingClassName}>
              Product
            </h2>
            <ul className="flex flex-col gap-2">
              {productLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className={footerLinkClassName}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-labelledby="footer-use-cases-heading" className="flex flex-col gap-3">
            <h2 id="footer-use-cases-heading" className={footerHeadingClassName}>
              Use cases
            </h2>
            <ul className="flex flex-col gap-2">
              {useCaseLinksPrimary.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className={footerLinkClassName}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-labelledby="footer-use-cases-more-heading" className="flex flex-col gap-3">
            <h2 id="footer-use-cases-more-heading" className={footerHeadingClassName}>
              Use cases
            </h2>
            <ul className="flex flex-col gap-2">
              {useCaseLinksSecondary.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className={footerLinkClassName}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="flex flex-col gap-3 border-t border-zinc-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <a href={SITE_URL} className={footerMetaLinkClassName}>
              {SITE_HOST}
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className={footerMetaLinkClassName}>
              {CONTACT_EMAIL}
            </a>
          </div>
          <nav aria-label="Company" className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {companyLinks.map((link) => (
              <Link key={link.href} href={link.href} className={footerMetaLinkClassName}>
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}
